<template>
  <div class="relative size-full">
    <div ref="sliderContainerRef" class="size-full" tabindex="0">
      <Swiper
        ref="swiperRef"
        :modules="[SwiperNavigation]"
        :loop="images.length > 1 && images.length <= 5"
        :slides-per-view="1"
        :space-between="0"
        :navigation="{
          nextEl: '.swiper-button-next-custom',
          prevEl: '.swiper-button-prev-custom',
        }"
        class="size-full"
        @swiper="onSwiper"
        @slide-change="onSlideChange"
      >
        <SwiperSlide
          v-for="(item, imageIndex) in images"
          :key="item.hash"
          class="!h-auto"
        >
          <SFLink
            :to="link"
            raw
            class="relative block size-full"
            @click="handleClick"
          >
            <SFProductImage
              v-if="item"
              :image="item"
              :alt="
                $t('product_image.alt_with_image_index', {
                  alt,
                  index: imageIndex + 1,
                  total: images.length,
                })
              "
              :image-loading="getImageLoading(imageIndex)"
              :preload="shouldPreload(imageIndex)"
              sizes="xs:50vw sm:50vw md:40vw lg:33vw xl:320px"
              class="absolute inset-0"
            />
          </SFLink>
        </SwiperSlide>
      </Swiper>
    </div>

    <SFSliderDotsIndicator
      v-if="isMobile"
      :current-index="currentIndex"
      :count="images.length"
      class="absolute bottom-2 left-1/2 z-50 -translate-x-1/2"
      @update-current-index="
        (index) => {
          currentIndex = index
          swiperInstance?.slideTo(index)
        }
      "
    />
  </div>
</template>

<script setup lang="ts">
import type { ProductImage as ProductImageType } from '@scayle/storefront-nuxt'
import { ref, useTemplateRef } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation as SwiperNavigation } from 'swiper/modules'
import { onKeyStroke } from '@vueuse/core'
import 'swiper/css'
import 'swiper/css/navigation'
import type { Swiper as SwiperType } from 'swiper'
import SFProductImage from '../../SFProductImage.vue'
import { PRODUCT_CARD_IMAGE_EAGER_LOAD_SIZE } from '~/constants'
import { SFLink } from '#storefront-ui/components'
import { useDefaultBreakpoints } from '#storefront-ui'
import SFSliderDotsIndicator from '#storefront-ui/components/core/SFSliderDotsIndicator.vue'

const { isProductHovered, images, productIndex } = defineProps<{
  link: string
  isProductHovered: boolean
  alt: string
  images: ProductImageType[]
  productIndex: number
}>()

const { smaller } = useDefaultBreakpoints()
const isMobile = smaller('md')
const swiperRef = useTemplateRef<{ swiper: SwiperType }>('swiperRef')
const sliderContainerRef = useTemplateRef<HTMLElement>('sliderContainerRef')
const swiperInstance = ref<SwiperType | null>(null)
const currentIndex = ref(0)

const onSwiper = (swiper: SwiperType) => {
  swiperInstance.value = swiper
}

const scrollImageIntoView = (
  index: number,
  scrollBehavior: ScrollBehavior = 'auto',
) => {
  if (swiperInstance.value) {
    swiperInstance.value.slideTo(index, scrollBehavior === 'smooth' ? 600 : 0)
  }
}

const onSlideChange = (swiper: SwiperType) => {
  // Get real index when loop is enabled
  currentIndex.value = swiper.realIndex
}

defineExpose({
  scrollImageIntoView,
})

const emit = defineEmits<{
  click: []
}>()

const handleClick = () => {
  emit('click')
}

const getImageLoading = (index: number) => {
  const shouldEagerLoad = productIndex < PRODUCT_CARD_IMAGE_EAGER_LOAD_SIZE
  return shouldEagerLoad && index === 0 ? 'eager' : 'lazy'
}

const shouldPreload = (index: number) => {
  const shouldPreload = productIndex < PRODUCT_CARD_IMAGE_EAGER_LOAD_SIZE
  return shouldPreload && index === 0
}

// Handle keyboard navigation - only when slider is focused or hovered
onKeyStroke(
  ['ArrowLeft', 'ArrowRight'],
  (event: KeyboardEvent) => {
    // Only handle keyboard events when the slider container is focused or product is hovered
    if (
      !swiperRef.value ||
      images.length <= 1 ||
      (document.activeElement !== sliderContainerRef.value && !isProductHovered)
    ) {
      return
    }

    event.preventDefault()
    if (event.key === 'ArrowLeft') {
      swiperRef.value.swiper.slidePrev?.()
    } else if (event.key === 'ArrowRight') {
      swiperRef.value.swiper.slideNext?.()
    }
  },
  { target: sliderContainerRef },
)
</script>

<style scoped>
/* Hide Swiper's default navigation buttons */
:deep(.swiper-button-next),
:deep(.swiper-button-prev) {
  display: none !important;
}

/* Ensure Swiper slides take full width and height */
:deep(.swiper-slide) {
  width: 100%;
  height: 100%;
}

/* Ensure Swiper wrapper matches original styling */
:deep(.swiper-wrapper) {
  display: flex;
  align-items: stretch;
}

/* Make sure the Swiper container matches size-full */
:deep(.swiper) {
  width: 100%;
  height: 100%;
}
</style>
